import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const sections = [
  {
    id: 'naturaleza',
    icon: 'ri-building-line',
    title: '1. Naturaleza del servicio',
    content: `Task Zone actúa como intermediario digital que facilita la conexión entre usuarios y proveedores de servicios, sin participar directamente en la ejecución de los mismos.`,
  },
  {
    id: 'registro',
    icon: 'ri-user-add-line',
    title: '2. Registro y veracidad de la información',
    content: `Los usuarios y proveedores se obligan a proporcionar información veraz, completa y actualizada. Task Zone podrá suspender o cancelar cuentas que incumplan esta obligación.`,
  },
  {
    id: 'uso',
    icon: 'ri-computer-line',
    title: '3. Uso de la plataforma',
    content: `Los usuarios se comprometen a utilizar la plataforma de forma lícita, respetuosa y conforme a las normas establecidas.`,
  },
  {
    id: 'contratacion',
    icon: 'ri-shake-hands-line',
    title: '4. Contratación dentro de la plataforma',
    content: `Queda prohibido realizar acuerdos o contrataciones fuera de Task Zone una vez establecido el contacto inicial, así como compartir datos de contacto con dicho fin.`,
  },
  {
    id: 'pagos',
    icon: 'ri-secure-payment-line',
    title: '5. Pagos y comisiones',
    content: `Los precios mostrados en la plataforma incluyen los cargos correspondientes al uso del servicio. Task Zone podrá aplicar comisiones tanto a usuarios como a proveedores conforme a su modelo de negocio.`,
  },
  {
    id: 'responsabilidad',
    icon: 'ri-shield-check-line',
    title: '6. Responsabilidad del servicio',
    content: `Los proveedores son responsables de la correcta ejecución de los servicios ofrecidos. Task Zone no garantiza resultados, pero podrá intervenir como mediador en caso de controversias.`,
  },
  {
    id: 'cancelaciones',
    icon: 'ri-calendar-close-line',
    title: '7. Cancelaciones y modificaciones',
    content: `Las condiciones de cancelación o modificación dependerán del servicio contratado y deberán ser aceptadas por las partes.`,
  },
  {
    id: 'evaluacion',
    icon: 'ri-star-line',
    title: '8. Sistema de evaluación',
    content: `Los usuarios podrán emitir valoraciones sobre los proveedores. Task Zone podrá eliminar contenido que considere inapropiado o falso.`,
  },
  {
    id: 'suspension',
    icon: 'ri-error-warning-line',
    title: '9. Suspensión o cancelación de cuentas',
    content: `Task Zone podrá suspender o eliminar cuentas en caso de incumplimiento de estos términos, uso indebido de la plataforma o intento de evasión del sistema.`,
  },
  {
    id: 'propiedad',
    icon: 'ri-copyright-line',
    title: '10. Propiedad intelectual',
    content: `Todos los elementos de la plataforma son propiedad de Task Zone y no podrán ser utilizados sin autorización.`,
  },
  {
    id: 'modificaciones',
    icon: 'ri-edit-line',
    title: '11. Modificaciones',
    content: `Task Zone se reserva el derecho de modificar los presentes términos en cualquier momento.`,
  },
];

const TermsPage = () => {
  const [activeSection, setActiveSection] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Términos y Condiciones de Uso | Task Zone';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Lee los Términos y Condiciones de uso de Task Zone: plataforma de servicios en Aguascalientes. Conoce las reglas de registro, contratación, pagos, responsabilidades y derechos de usuarios y proveedores.');

    const schemaId = 'schema-terms';
    if (!document.getElementById(schemaId)) {
      const script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Términos y Condiciones de Uso – Task Zone',
        url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/terminos-y-condiciones`,
        description: 'Términos y condiciones de uso de la plataforma Task Zone para usuarios y proveedores de servicios en Aguascalientes.',
        dateModified: '2026-04-07',
        publisher: {
          '@type': 'Organization',
          name: 'Task Zone',
          url: import.meta.env.VITE_SITE_URL || 'https://example.com',
        },
      });
      document.head.appendChild(script);
    }

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  const toggleSection = (id: string) => {
    setActiveSection(activeSection === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      {/* Hero */}
      <section className="pt-16 bg-gradient-to-br from-orange-500 to-orange-600">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-2xl mx-auto mb-5">
            <i className="ri-file-list-3-line text-white text-3xl" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">Términos y Condiciones de Uso</h1>
          <p className="text-orange-100 text-sm max-w-xl mx-auto mb-4">
            Última actualización: 7 de abril de 2026
          </p>
          <p className="text-orange-100 text-sm max-w-2xl mx-auto">
            El acceso y uso de la plataforma Task Zone implica la aceptación plena de los presentes términos y condiciones.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <div className="flex flex-col lg:flex-row gap-10">

          {/* Sidebar index */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="sticky top-24 bg-gray-50 border border-gray-100 rounded-xl p-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-4">Contenido</p>
              <ul className="space-y-1">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      onClick={() => setActiveSection(s.id)}
                      className="flex items-center gap-2 text-xs text-gray-600 hover:text-orange-600 py-1.5 px-2 rounded-lg hover:bg-orange-50 transition-colors cursor-pointer"
                    >
                      <i className={`${s.icon} text-orange-400 flex-shrink-0`} />
                      <span className="leading-tight">{s.title.replace(/^\d+\.\s/, '')}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Main content */}
          <div className="flex-1 space-y-4">
            {sections.map((s) => (
              <div
                key={s.id}
                id={s.id}
                className="border border-gray-100 rounded-xl overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => toggleSection(s.id)}
                  className="w-full flex items-center justify-between px-6 py-4 bg-gray-50 hover:bg-orange-50 transition-colors cursor-pointer text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                      <i className={`${s.icon} text-orange-500 text-sm`} />
                    </div>
                    <span className="font-semibold text-gray-800 text-sm">{s.title}</span>
                  </div>
                  <i className={`ri-arrow-down-s-line text-gray-400 transition-transform duration-200 ${activeSection === s.id ? 'rotate-180' : ''}`} />
                </button>
                {activeSection === s.id && (
                  <div className="px-6 py-5 bg-white border-t border-gray-100">
                    <p className="text-gray-600 text-sm leading-relaxed">{s.content}</p>
                  </div>
                )}
              </div>
            ))}

            {/* Contact box */}
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 mt-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-orange-500 rounded-lg flex-shrink-0">
                  <i className="ri-question-line text-white text-lg" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm mb-1">¿Tienes dudas sobre estos términos?</h3>
                  <p className="text-gray-600 text-xs leading-relaxed mb-3">
                    Si tienes preguntas sobre nuestros Términos y Condiciones, puedes contactarnos en cualquier momento.
                  </p>
                  <a href="mailto:taskzone.app@gmail.com" className="inline-flex items-center gap-2 text-orange-600 text-xs font-semibold hover:underline">
                    <i className="ri-mail-line" /> taskzone.app@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related links */}
      <section className="bg-gray-50 border-t border-gray-100 py-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-500 text-sm">También puede interesarte:</p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/politica-de-privacidad"
              className="inline-flex items-center gap-2 bg-white border border-gray-200 text-gray-700 text-xs font-medium px-4 py-2 rounded-lg hover:border-orange-300 hover:text-orange-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-shield-user-line text-orange-500" /> Política de Privacidad
            </Link>
            <Link
              to="/sobre-task-zone"
              className="inline-flex items-center gap-2 bg-white border border-gray-200 text-gray-700 text-xs font-medium px-4 py-2 rounded-lg hover:border-orange-300 hover:text-orange-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-information-line text-orange-500" /> Sobre Task Zone!
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsPage;
